import { makeSuccessToast, msg, makeErrorToast } from '../../utils/makeToast'
import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import { API, graphqlOperation } from '@aws-amplify/api'
import * as mutations from '../../graphql/mutations'
import * as queries from '../../graphql/queries'
import { getEmailDomain } from '@src/utils/miscellaneous'

export const createCompanyInfo = createAsyncThunk(
  'settings/createCompanyInfo',
  async (userInfo, { rejectWithValue }) => {
    try {
      const companyExists = await API.graphql(
        graphqlOperation(queries.getCompany, { id: userInfo.tenantId })
      )

      // Checks if company exists in db, if it does it return the compay details else it creates the company
      if (!!companyExists.data.getCompany) {
        return companyExists.data.getCompany
      }
      const createdCompanyInfoDetails = {
        id: userInfo.tenantId,
        tenantId: userInfo.tenantId,
      }
      const userInformation = await API.graphql(
        graphqlOperation(mutations.createCompany, { input: createdCompanyInfoDetails })
      )
      const emailDomain = getEmailDomain(userInfo.username)

      await API.graphql(
        graphqlOperation(mutations.createUniqueEmailId, {
          companyId: userInfo.tenantId,
          emailDomain,
        })
      )
      return userInformation.data.createCompany
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const createUserInfo = createAsyncThunk(
  'settings/createUserInfo',
  async (userInfo, { rejectWithValue }) => {
    try {
      // Check first if the user is present in the db
      const userExists = await API.graphql(
        graphqlOperation(queries.getUserInfo, { id: userInfo.attributes.sub })
      )
      //  If userExists then it exits the function else it creates the user in db
      if (!!userExists.data.getUserInfo) {
        return userExists.data.getUserInfo
      }

      const createdUserInfoDetails = {
        id: userInfo.attributes.sub,
        email: userInfo.username,
        tenantId: userInfo.tenantId,
      }
      const userInformation = await API.graphql(
        graphqlOperation(mutations.createUserInfo, { input: createdUserInfoDetails })
      )
      return userInformation.data.createUserInfo
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const fetchCompanyInfo = createAsyncThunk(
  'settings/fetchCompanyInfo',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState()
      const userInfo = auth.userInfo
      // We first query to get the latest _version number from db then update the model
      const companyInfoDetails = await API.graphql(
        graphqlOperation(queries.getCompany, { id: userInfo.tenantId })
      )
      return companyInfoDetails.data.getCompany
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const fetchUserInfo = createAsyncThunk(
  'settings/fetchUserInfo',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState()
      const userInfo = auth.userInfo
      // We first query to get the latest _version number from db then update the model
      const userInfoDetails = await API.graphql(
        graphqlOperation(queries.getUserInfo, { id: userInfo.attributes?.sub })
      )
      return userInfoDetails.data.getUserInfo
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const updateCompanyInfo = createAsyncThunk(
  'settings/updateCompanyInfo',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState()
      const userInfo = auth.userInfo
      // We first query to get the latest _version number from db then update the model
      const oldCompanyInfoDetails = await API.graphql(
        graphqlOperation(queries.getCompany, { id: userInfo.tenantId })
      )
      // Updated userInfo details
      const updatedCompanyInfoDetails = {
        id: userInfo.tenantId,
        ...data,
        _version: oldCompanyInfoDetails.data.getCompany._version,
      }
      const updatedCompanyInfo = await API.graphql(
        graphqlOperation(mutations.updateCompany, { input: updatedCompanyInfoDetails })
      )
      return updatedCompanyInfo.data?.updateCompany
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const updateUserInfo = createAsyncThunk(
  'settings/updateUserInfo',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState()
      const userInfo = auth.userInfo
      // We first query to get the latest _version number from db then update the model
      const oldUserInfoDetails = await API.graphql(
        graphqlOperation(queries.getUserInfo, { id: userInfo.attributes?.sub })
      )
      // Updated userInfo details
      const updatedUserInfoDetails = {
        id: userInfo.attributes?.sub,
        ...data,
        _version: oldUserInfoDetails.data.getUserInfo._version,
      }
      const updatedUserInfo = await API.graphql(
        graphqlOperation(mutations.updateUserInfo, { input: updatedUserInfoDetails })
      )
      return updatedUserInfo.data?.updateUserInfo
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)
export const createFreelancerInfo = createAsyncThunk(
  'settings/createFreelancerInfo',
  async ({ userInfo, email }, { rejectWithValue }) => {
    try {
      const {
        data: {
          freelancersById: { items },
        },
      } = await API.graphql(
        graphqlOperation(queries.freelancersById, {
          freelancerTenantId: userInfo.tenantId,
        })
      )
      if (userInfo.groups.includes('freelancer') && items.length === 0) {
        const newItem = {
          id: nanoid(),
          freelancerTenantId: userInfo.tenantId,
          email: email,
        }
        try {
          await API.graphql(graphqlOperation(mutations.createFreelancers, { input: newItem }))
        } catch (err) {
          makeErrorToast(msg)
        }
      }
    } catch (err) {
      makeErrorToast(err.message)
      return rejectWithValue(err)
    }
  }
)

export const settingsSlice = createSlice({
  name: 'settings',
  initialState: {
    companyInfo: {},
    userInformation: {},
    fetchCompanyInfoApiStatus: 'idle',
    updateCompanyInfoApiStatus: 'idle',
    updateUserInfoApiStatus: 'idle',
    isMenuOpen: null,
    sort: JSON.parse(window.localStorage.getItem('sort')) ?? 'date',
    isUserProfileModalOpen: false,
    isChangePasswordModalOpen: false,
    isAccountSettingsOpen: false,
    isImageUploadOpen: false,
    isLogoutOpen: false,
    companyLogoUrl: null,
    offlineLogo: null,
  },
  reducers: {
    setIsMenuOpen: (state, action) => {
      state.isMenuOpen = action.payload
    },
    setSortBy: (state, action) => {
      window.localStorage.setItem('sort', JSON.stringify(action.payload))
      state.sort = action.payload
    },
    setIsUserProfileModalOpen: (state, action) => {
      state.isUserProfileModalOpen = action.payload
    },
    setIsChangePasswordModalOpen: (state, action) => {
      state.isChangePasswordModalOpen = action.payload
    },
    setIsAccountSettingsOpen: (state, action) => {
      state.isAccountSettingsOpen = action.payload
    },
    setIsImageUploadOpen: (state, action) => {
      state.isImageUploadOpen = action.payload
    },
    setIsLogoutOpen: (state, action) => {
      state.isLogoutOpen = action.payload
    },
    setCompanyLogoUrl: (state, action) => {
      state.companyLogoUrl = action.payload
    },
    setCompanyInfo: (state, action) => {
      state.companyInfo = action.payload
    },
    setOfflineLogo: (state, action) => {
      state.offlineLogo = action.payload
    },
  },
  extraReducers: {
    [createCompanyInfo.fulfilled]: (state, action) => {
      state.companyInfo = action.payload
    },
    [createCompanyInfo.rejected]: (state, action) => {
      state.error = action.error.message
    },
    [createUserInfo.fulfilled]: (state, action) => {
      state.userInformation = action.payload
    },
    [createUserInfo.rejected]: (state, action) => {
      state.error = action.error.message
    },
    [fetchCompanyInfo.pending]: (state, action) => {
      state.fetchCompanyInfoApiStatus = 'loading'
    },
    [fetchCompanyInfo.fulfilled]: (state, action) => {
      state.companyInfo = action.payload
      state.fetchCompanyInfoApiStatus = 'succeeded'
    },
    [fetchCompanyInfo.rejected]: (state, action) => {
      state.error = action.error.message
      state.fetchCompanyInfoApiStatus = 'failed'
    },
    [fetchUserInfo.fulfilled]: (state, action) => {
      state.userInformation = action.payload
    },
    [fetchUserInfo.rejected]: (state, action) => {
      state.error = action.error.message
    },
    [updateCompanyInfo.pending]: (state, action) => {
      state.updateCompanyInfoApiStatus = 'loading'
    },
    [updateCompanyInfo.fulfilled]: (state, action) => {
      state.updateCompanyInfoApiStatus = 'succeeded'
      state.companyInfo = action.payload
      if (!action.meta.arg.logoImage) {
        state.isAccountSettingsOpen = false
        makeSuccessToast('Information succesfully updated')
      }
    },
    [updateCompanyInfo.rejected]: (state, action) => {
      state.updateCompanyInfoApiStatus = 'failed'
      state.error = action.error.message
    },
    [updateUserInfo.pending]: (state, action) => {
      state.updateUserInfoApiStatus = 'loading'
    },
    [updateUserInfo.fulfilled]: (state, action) => {
      state.updateUserInfoApiStatus = 'succeeded'
      makeSuccessToast('Information succesfully updated')
      state.isUserProfileModalOpen = false
      state.userInformation = action.payload
    },
    [updateUserInfo.rejected]: (state, action) => {
      state.updateUserInfoApiStatus = 'failed'
      state.error = action.error.message
    },
  },
})

export const {
  setIsMenuOpen,
  setSortBy,
  setIsAccountSettingsOpen,
  setIsChangePasswordModalOpen,
  setIsUserProfileModalOpen,
  setIsImageUploadOpen,
  setIsLogoutOpen,
  setCompanyLogoUrl,
  setCompanyInfo,
  setOfflineLogo,
} = settingsSlice.actions

export default settingsSlice.reducer
